import React, { useMemo, useCallback, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import PulseLoader from "react-spinners/PulseLoader";
import IconMicro from "@images/chatvoice/micro-icon.svg";
import IconPause from "@images/chatvoice/pause-icon.svg";
import AudioClick from "@audio/select-click.wav";

const shake = keyframes`
  0%,100% { transform: translateX(0); }
  25%      { transform: translateX(-4px); }
  50%      { transform: translateX(4px);  }
  75%      { transform: translateX(-4px); }
`;

const Box = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 30px;
  @media (max-width: 768px) {
    padding-bottom: 0;
  }
`;

const ButtonWrapper = styled.button`
  position: relative;
  width: 100px;
  height: 100px;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;

  &:active {
    animation: ${shake} 0.3s linear;
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:disabled:hover {
    cursor: not-allowed;
  }
`;

const BgSvg = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }
`;

const IconWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const Text = styled.div`
  color: #fff;
  text-align: center;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const LoaderWrapper = styled.div`
  margin-top: 20px;
`;

export default function PulseButtonAnimate({
  isRecording,
  onButtonClick,
  isLoadingChatVoice,
  statusGenerateAudio,
  audioStarted,
}) {
  const clickAudio = useMemo(() => new Audio(AudioClick), []);
  useEffect(() => {
    clickAudio.load();
  }, [clickAudio]);

  const handleClick = useCallback(() => {
    if (!isLoadingChatVoice && !audioStarted) {
      clickAudio.play();
      onButtonClick();
    }
  }, [isLoadingChatVoice, audioStarted, onButtonClick, clickAudio]);

  const isProcessing =
    statusGenerateAudio === "streaming" || isLoadingChatVoice;

  if (isProcessing) {
    return (
      <Box>
        <LoaderWrapper>
          <PulseLoader color="#fff" size={18} />
        </LoaderWrapper>
        {/* <Text>Thinking...</Text> */}
      </Box>
    );
  }

  return (
    <Box>
      <Text>{!audioStarted && !isRecording ? "Press to Speak" : "Listening.... Press when you're done speaking"}</Text>
      <ButtonWrapper
        onClick={handleClick}
        disabled={isLoadingChatVoice || audioStarted}
      >
        <BgSvg
          dangerouslySetInnerHTML={{
            __html: `<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100" fill="none">
  <g filter="url(#filter0_ii_62_44)">
    <g clip-path="url(#clip0_62_44)">
      <path d="M0 50C0 22.3858 22.3858 0 50 0C77.6142 0 100 22.3858 100 50C100 77.6142 77.6142 100 50 100C22.3858 100 0 77.6142 0 50Z" fill="white"/>
      <g filter="url(#filter1_f_62_44)">
        <ellipse cx="46.3002" cy="46.2097" rx="46.3002" ry="46.2097" transform="matrix(-0.748469 0.66317 -0.735603 -0.677413 137.293 33.606)" fill="url(#paint0_linear_62_44)"/>
      </g>
      <g opacity="0.7" filter="url(#filter2_f_62_44)">
        <path d="M58.2133 47.9134C65.5342 72.5289 40.3089 94.8793 13.2046 87.7927C-13.4597 80.8212 -21.9719 50.4896 -2.18445 32.9572C17.603 15.4248 51.0112 23.6976 58.2133 47.9134Z" fill="url(#paint1_linear_62_44)"/>
      </g>
    </g>
  </g>
  <defs>
    <filter id="filter0_ii_62_44" x="-4" y="-4" width="108" height="108" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
      <feFlood flood-opacity="0" result="BackgroundImageFix"/>
      <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
      <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
      <feOffset dx="4" dy="4"/>
      <feGaussianBlur stdDeviation="2"/>
      <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
      <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0.666667 0 0 0 0 1 0 0 0 0.25 0"/>
      <feBlend mode="normal" in2="shape" result="effect1_innerShadow_62_44"/>
      <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
      <feOffset dx="-4" dy="-4"/>
      <feGaussianBlur stdDeviation="2"/>
      <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
      <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0.235659 0 0 0 0 0.785529 0 0 0 0.25 0"/>
      <feBlend mode="normal" in2="effect1_innerShadow_62_44" result="effect2_innerShadow_62_44"/>
    </filter>
    <filter id="filter1_f_62_44" x="-14.1654" y="-45.1105" width="165.625" height="156.237" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
      <feFlood flood-opacity="0" result="BackgroundImageFix"/>
      <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
      <feGaussianBlur stdDeviation="17.135" result="effect1_foregroundBlur_62_44"/>
    </filter>
    <filter id="filter2_f_62_44" x="-38.7444" y="-2.10919" width="123.943" height="116.936" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
      <feFlood flood-opacity="0" result="BackgroundImageFix"/>
      <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
      <feGaussianBlur stdDeviation="12.8512" result="effect1_foregroundBlur_62_44"/>
    </filter>
    <linearGradient id="paint0_linear_62_44" x1="9.57167" y1="6.36865" x2="74.0373" y2="92.5405" gradientUnits="userSpaceOnUse">
      <stop stop-color="white"/>
      <stop offset="0.424373" stop-color="#00D9FF" stop-opacity="0.74"/>
      <stop offset="1" stop-color="#00FFFF" stop-opacity="0"/>
    </linearGradient>
    <linearGradient id="paint1_linear_62_44" x1="74.8689" y1="103.915" x2="-17.2758" y2="-0.0813319" gradientUnits="userSpaceOnUse">
      <stop stop-color="#004DFF"/>
      <stop offset="1" stop-color="#00A3FF" stop-opacity="0"/>
    </linearGradient>
    <clipPath id="clip0_62_44">
      <path d="M0 50C0 22.3858 22.3858 0 50 0C77.6142 0 100 22.3858 100 50C100 77.6142 77.6142 100 50 100C22.3858 100 0 77.6142 0 50Z" fill="white"/>
    </clipPath>
  </defs></svg>`,
          }}
        />
        <IconWrapper>
          <img
            src={isRecording ? IconPause : IconMicro}
            alt={isRecording ? "Stop" : "Record"}
          />
        </IconWrapper>
      </ButtonWrapper>
    </Box>
  );
}
